/**
 * MenuLibrary — the reusable menu catalog (Menus tab). Build a menu once and
 * reuse/edit it across events (Toast/Square-style shared menu entities). The
 * parent owns the live `menus` subscription and passes it in.
 */
import React, { useState } from 'react';
import { createMenu, updateMenu, deleteMenu } from '../eventsCloud';
import { timeAgo } from '../eventUtils';
import MenuEditorModal from './MenuEditorModal';
import ConfirmModal from './ConfirmModal';
import { t } from '../strings';

export default function MenuLibrary({ menus, user, onToast }) {
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [deleting, setDeleting] = useState(null);

  const save = async (data) => {
    if (editing && editing.id) await updateMenu(editing.id, data);
    else await createMenu(data, user);
    onToast?.(t('menuSaved'));
  };

  const duplicate = async (m) => {
    await createMenu({ name: `${m.name || 'Menu'} (copy)`, items: m.items || [] }, user);
    onToast?.('Menu duplicated');
  };

  const confirmDelete = async () => {
    const m = deleting;
    setDeleting(null);
    if (m) { await deleteMenu(m.id); onToast?.('Menu deleted'); }
  };

  return (
    <div>
      <div className="ev-libbar">
        <p className="ev__sub" style={{ margin: 0 }}>{t('menusSubtitle')}</p>
        <button className="ev__btn ev__btn--primary" onClick={() => setEditing(null)}>+ {t('newMenu')}</button>
      </div>

      {menus === null ? (
        <div className="ev__grid">{[0, 1].map((i) => <div key={i} className="ev__card ev__card--skeleton" />)}</div>
      ) : menus.length === 0 ? (
        <div className="ev__empty">
          <div className="ev__empty-icon">🍽️</div>
          <h3>{t('noMenus')}</h3>
          <p>Save a menu here to quickly load it into any event.</p>
          <button className="ev__btn ev__btn--primary" onClick={() => setEditing(null)}>+ {t('newMenu')}</button>
        </div>
      ) : (
        <div className="ev__grid">
          {menus.map((m) => (
            <div key={m.id} className="ev__card ev-menucard">
              <div className="ev__card-top">
                <span className="ev-menucard__icon">🍽️</span>
                <span className="ev__card-count">{(m.items || []).length} {t('items')}</span>
              </div>
              <h3 className="ev__card-title">{m.name || 'Untitled menu'}</h3>
              <div className="ev__card-meta">
                {(m.items || []).slice(0, 3).map((it) => it.name).filter(Boolean).join(' · ') || 'No items'}
                {(m.items || []).length > 3 ? ' …' : ''}
              </div>
              <div className="ev__card-meta">{m.updatedAt ? `Updated ${timeAgo(m.updatedAt)}` : ''}</div>
              <div className="ev__card-actions">
                <button className="ev__btn ev__btn--sm ev__btn--wide" onClick={() => setEditing(m)}>✎ {t('edit')}</button>
                <button className="ev__btn ev__btn--sm" onClick={() => duplicate(m)}>⧉ {t('duplicate')}</button>
                <span className="ev__card-actions-spacer" />
                <button className="ev__iconbtn ev__iconbtn--danger" title={t('del')} onClick={() => setDeleting(m)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <MenuEditorModal
        open={editing !== undefined}
        initial={editing || null}
        onClose={() => setEditing(undefined)}
        onSave={save}
      />
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete menu?"
        message={`Delete "${deleting?.name || 'this menu'}" from your library? Events already using it keep their own copy.`}
        confirmLabel="Delete"
      />
    </div>
  );
}
